import { useDroppable, type UniqueIdentifier, type Data } from "@dnd-kit/core";
import { cloneElement } from "react";

type RenderFunction = (props: { isOver: boolean }) => React.ReactNode;

type DroppableProps = {
  data?: Data;
  id: UniqueIdentifier;
} & React.HTMLAttributes<HTMLElement> &
  (
    | { children: React.ReactNode; render?: never }
    | { children?: never; render: RenderFunction }
  );

export const Droppable = ({
  children,
  className,
  data,
  id,
  render,
  ...props
}: DroppableProps) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
    data,
  });

  const isOverClasses = [isOver ? "isOver" : undefined].join(" ");

  // If render prop is provided, use it instead of children
  if (render) {
    const renderedContent = render({ isOver });

    return cloneElement(renderedContent as React.ReactElement, {
      ref: setNodeRef,
      ...props,
      className: [
        (renderedContent as React.ReactElement).props.className,
        className,
        isOverClasses,
      ]
        .filter(Boolean)
        .join(" "),
    });
  }

  // Default implementation with children
  return cloneElement(children as React.ReactElement, {
    ref: setNodeRef,
    ...props,
    className: [
      (children as React.ReactElement).props.className,
      className,
      isOverClasses,
    ]
      .filter(Boolean)
      .join(" "),
  });
};
